import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { STAGES, fmtINR, fmtDate, stagesForPipeline, PIPELINE_TYPE_LABEL } from "@/lib/format";
import { isBackwardMove } from "@/lib/stageRules";
import StageMoveConfirmDialog from "@/components/StageMoveConfirmDialog";
import ManufacturerFilter from "@/components/ManufacturerFilter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GripVertical, Calendar, AlertTriangle, Plus, Clock, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import EmployeeFilter from "@/components/EmployeeFilter";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { NewDealDialog } from "@/pages/Deals";

function stageTenure(enteredAt) {
  if (!enteredAt) return null;
  const d = new Date(enteredAt);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  return { days, label: fmtDate(enteredAt) };
}

function DealCard({ deal, onDragStart }) {
  const navigate = useNavigate();
  const fuStatus = deal.followup_status;
  const fuColor =
    fuStatus === "overdue" ? "bg-red-500" : fuStatus === "today" ? "bg-amber-500" : fuStatus === "upcoming" ? "bg-emerald-500" : "bg-zinc-300";
  const tenure = stageTenure(deal.stage_entered_at);
  // Subtle nudge when a deal has been stuck in a stage for too long — the
  // whole point of surfacing tenure on the card is to spot stalls fast.
  const tenureTone =
    !tenure ? "bg-muted/50 text-muted-foreground border-border/60"
    : tenure.days >= 21 ? "bg-red-50 text-red-700 border-red-200"
    : tenure.days >= 10 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-muted/60 text-muted-foreground border-border/60";
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal.id)}
      onClick={() => navigate(`/deals/${deal.id}`)}
      data-testid={`deal-card-${deal.id}`}
      className="cursor-grab active:cursor-grabbing rounded-2xl bg-card border border-border/60 p-3.5 shadow-soft card-lift"
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${fuColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-snug truncate">{deal.title}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{deal.client_name || "Unlinked"}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-display text-base font-extrabold text-primary-700">{fmtINR(deal.value)}</span>
        {deal.next_followup_date && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
              fuStatus === "overdue"
                ? "bg-red-50 text-red-700 border-red-200"
                : fuStatus === "today"
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
          >
            {fuStatus === "overdue" ? <AlertTriangle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
            {fmtDate(deal.next_followup_date)}
          </span>
        )}
      </div>
      {tenure && (
        <div className="mt-2">
          <span
            data-testid={`deal-card-tenure-${deal.id}`}
            title={`In stage since ${tenure.label}`}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tenureTone}`}
          >
            <Clock className="h-3 w-3" />
            {tenure.label} · {tenure.days}d
          </span>
        </div>
      )}
      {/* Training pipeline-specific: surface the scheduled date window on
          the Kanban tile so trainers can see "when this is happening" at
          a glance without opening the card. */}
      {deal.pipeline_type === "training" && (deal.training_start_date || deal.training_end_date) && (
        <div className="mt-2">
          <span
            data-testid={`deal-card-training-window-${deal.id}`}
            title="Training schedule"
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200"
          >
            <Calendar className="h-3 w-3" />
            {deal.training_start_date && deal.training_end_date && deal.training_start_date !== deal.training_end_date
              ? `${fmtDate(deal.training_start_date)} → ${fmtDate(deal.training_end_date)}`
              : fmtDate(deal.training_start_date || deal.training_end_date)}
          </span>
        </div>
      )}
      {(deal.manufacturer_names || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`deal-card-suppliers-${deal.id}`}>
          {deal.manufacturer_names.map((m) => (
            <span
              key={m}
              className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
              title="Supplier"
            >
              {m}
            </span>
          ))}
        </div>
      )}
      {deal.last_interaction_summary && (
        <p className="mt-2 text-[11px] text-muted-foreground line-clamp-2 italic">"{deal.last_interaction_summary}"</p>
      )}
      <p className="mt-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{deal.owner_name || ""}</p>
    </div>
  );
}

export default function Pipeline() {
  // Route param: /pipeline/product | service | training (default product)
  const { pipelineType: ptParam } = useParams();
  const pipelineType = ["product", "service", "training"].includes(ptParam) ? ptParam : "product";
  const stagesCfg = stagesForPipeline(pipelineType);
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const [ownerId, setOwnerId] = useState("");
  const [manufacturerId, setManufacturerId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  // Pending stage move awaiting confirmation. Keeps the deal id, the source
  // stage (for the dialog header) and the target stage so onConfirm can fire
  // the API call without re-reading the dataTransfer payload.
  const [pendingMove, setPendingMove] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  // 37.docx: show only 2 cards per stage by default; per-column "See more".
  const [expanded, setExpanded] = useState({});
  const scrollRef = useRef(null);
  const scrollByStage = (dir) => scrollRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  const openNewDeal = () => setDialogOpen(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pipeline", {
        params: {
          pipeline_type: pipelineType,
          owner_id: ownerId || undefined,
          manufacturer_id: manufacturerId || undefined,
        },
      });
      setGrouped(data.deals);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setExpanded({}); /* eslint-disable-next-line */ }, [ownerId, manufacturerId, pipelineType]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  const onDragStart = (e, id) => {
    e.dataTransfer.setData("deal-id", id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("deal-id");
    if (!id) return;
    // Find the dragged deal across the grouped buckets to determine its
    // current stage. Without this we can't tell forward from backward.
    let fromStage = null;
    for (const [k, items] of Object.entries(grouped)) {
      if ((items || []).some((d) => d.id === id)) { fromStage = k; break; }
    }
    if (fromStage === stage) return; // dropped on same column — no-op
    if (isBackwardMove(fromStage, stage)) {
      toast.error("Pipeline is one-way. Deals cannot move backward.");
      return; // visual snap-back: we never optimistically updated anyway
    }
    // Forward move — require explicit confirmation. The dialog calls
    // confirmMove on Yes which actually fires the API request.
    setPendingMove({ id, fromStage, toStage: stage });
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    setMoveBusy(true);
    try {
      await api.post(`/deals/${pendingMove.id}/stage`, { stage: pendingMove.toStage });
      toast.success(`Moved to ${stagesCfg.find((s) => s.key === pendingMove.toStage)?.label}`);
      setPendingMove(null);
      load();
    } catch (err) {
      toast.error(fmtError(err) || "Could not move deal");
    } finally {
      setMoveBusy(false);
    }
  };

  const onDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(stage);
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8 space-y-4">
        <Skeleton className="h-10 w-48 rounded-2xl" />
        <div className="flex gap-4 overflow-x-auto">
          {stagesCfg.map((s) => (
            <Skeleton key={s.key} className="h-96 w-72 rounded-3xl shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-full">
      {pullIndicator}
      <header className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{PIPELINE_TYPE_LABEL[pipelineType]}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1" data-testid="pipeline-title">Move deals forward.</h1>
          <p className="text-sm text-muted-foreground mt-2">Drag any card to a different stage. Tap to open.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <EmployeeFilter value={ownerId} onChange={setOwnerId} testId="pipeline-employee-filter" />
          <ManufacturerFilter value={manufacturerId} onChange={setManufacturerId} testId="pipeline-supplier-filter" />
          <Button onClick={openNewDeal} className="rounded-2xl h-10" data-testid="pipeline-new-deal-btn">
            <Plus className="h-4 w-4 mr-1" /> New deal
          </Button>
        </div>
      </header>

      {/* Pipeline-type switcher — gives mobile users (and anyone on the
          standalone Pipeline route) a one-tap way to hop between Product,
          Service and Training kanbans without going back to the sidebar. */}
      <div
        className="mb-4 sm:mb-6 inline-flex p-1 rounded-2xl bg-muted/50 border border-border/60 gap-1"
        data-testid="pipeline-type-switcher"
      >
        {[
          { v: "product",  l: "Product" },
          { v: "service",  l: "Service" },
          { v: "training", l: "Training" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => navigate(`/pipeline/${opt.v}`)}
            data-testid={`pipeline-type-tab-${opt.v}`}
            className={`px-3 sm:px-4 h-9 rounded-xl text-xs sm:text-sm font-semibold tap transition ${
              pipelineType === opt.v
                ? "bg-card text-foreground shadow-sm border border-border/60"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="kanban-scroll flex gap-4 sm:gap-5 overflow-x-auto pb-2 -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10">
        {stagesCfg.map((s) => {
          const items = grouped[s.key] || [];
          const total = items.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
          const isOpen = !!expanded[s.key];
          const visible = isOpen ? items : items.slice(0, 2);
          const hiddenCount = items.length - visible.length;
          return (
            <div
              key={s.key}
              onDrop={(e) => onDrop(e, s.key)}
              onDragOver={(e) => onDragOver(e, s.key)}
              onDragLeave={() => setDragOver(null)}
              data-testid={`pipeline-column-${s.key}`}
              className={`shrink-0 w-[280px] sm:w-[300px] rounded-3xl bg-muted/40 border ${
                dragOver === s.key ? "border-primary border-2 bg-primary-50/40" : "border-border/40"
              } transition`}
            >
              <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.color}`}>
                    {s.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs font-semibold text-muted-foreground">{items.length}</span>
                  {s.key === stagesCfg[0].key && (
                    <button
                      type="button"
                      onClick={openNewDeal}
                      data-testid={`pipeline-col-add-${s.key}`}
                      aria-label={`Add deal to ${s.label}`}
                      className="h-6 w-6 grid place-items-center rounded-full bg-card border border-border/60 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition tap"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="px-3 pb-1 text-xs text-muted-foreground font-medium">{fmtINR(total)}</div>
              <div className="p-3 pt-2 space-y-2.5 min-h-[120px]">
                {items.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/80 p-6 text-center text-xs text-muted-foreground">
                    Drop deals here
                  </div>
                ) : (
                  visible.map((d) => <DealCard key={d.id} deal={d} onDragStart={onDragStart} />)
                )}
                {items.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [s.key]: !isOpen }))}
                    data-testid={`pipeline-see-more-${s.key}`}
                    className="w-full flex items-center justify-center gap-1 rounded-2xl border border-border/60 bg-card py-2 text-xs font-semibold text-primary hover:bg-primary-50/60 transition tap"
                  >
                    {isOpen ? (
                      <><ChevronUp className="h-3.5 w-3.5" /> See less</>
                    ) : (
                      <><ChevronDown className="h-3.5 w-3.5" /> See more ({hiddenCount})</>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 37.docx: left/right stage navigation directly below the columns —
          reachable without scrolling to the bottom of the page. */}
      <div className="mt-3 mb-6 flex items-center justify-center gap-3" data-testid="pipeline-nav-controls">
        <Button
          variant="outline"
          onClick={() => scrollByStage(-1)}
          data-testid="pipeline-nav-left"
          aria-label="Scroll to previous stages"
          className="rounded-full h-10 w-10 p-0"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="text-xs text-muted-foreground font-medium select-none">Move between stages</span>
        <Button
          variant="outline"
          onClick={() => scrollByStage(1)}
          data-testid="pipeline-nav-right"
          aria-label="Scroll to next stages"
          className="rounded-full h-10 w-10 p-0"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      <NewDealDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={load}
        pipelineType={pipelineType}
      />

      <StageMoveConfirmDialog
        open={!!pendingMove}
        onOpenChange={(v) => { if (!v) setPendingMove(null); }}
        fromStage={pendingMove?.fromStage}
        toStage={pendingMove?.toStage}
        busy={moveBusy}
        onConfirm={confirmMove}
      />
    </div>
  );
}
